import walletImage from "@/assets/audiolab/payout-wallet.png";
import LeftNav from "@/components/nav/LeftNav";
import {
  StripeConnectProvider,
  useStripeConnect,
} from "@/components/providers/StripeConnectProvider";
import ContainerWrapper from "@/components/ui/ContainerWrapper";
import { analytics, EventEnum } from "@/lib/analytics";
import type { SalesSummaryDto } from "@/lib/lutely-api/gen/dtos";
import {
  useStripeControllerGetDashboardLink,
  useStripeControllerGetOnboardingLink,
  useStripeControllerGetPendingTransfersSummary,
  useStripeControllerGetStripeAccountCapabilities,
} from "@/lib/lutely-api/gen/stripe/stripe";
import { Box, Button, Flex, Spinner, Text } from "@radix-ui/themes";
import { ConnectPayouts } from "@stripe/react-connect-js";
import { useGateValue } from "@statsig/react-bindings";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { toast } from "react-toastify";
import { NoSalesYet } from "./-components/sales/NoSalesYet";
import { PendingTransfersBanner } from "./-components/sales/PendingTransfersBanner";
import { SalesDashboard } from "./-components/sales/SalesDashboard";
import { SalesSummaryCards } from "./-components/sales/SalesSummaryCards";

export const Route = createFileRoute("/audiolab/sales")({
  component: SalesPage,
  // ?setup=payout sends the artist to Stripe-hosted onboarding — links from
  // anywhere (pending-transfer banners, publish errors) can start the setup.
  validateSearch: (search: Record<string, unknown>): { setup?: "payout" } =>
    search.setup === "payout" ? { setup: "payout" } : {},
  head: () => ({
    meta: [
      { title: "Sales & Earnings | Lutely" },
      {
        name: "description",
        content:
          "View your sales, earnings, balances, and payouts from your Lutely marketplace.",
      },
    ],
  }),
});

function SalesPage() {
  const navigate = useNavigate();
  const { setup } = Route.useSearch();
  const { data: capabilities, isLoading: isLoadingCapabilities } =
    useStripeControllerGetStripeAccountCapabilities();

  const showOnboarding = setup === "payout";
  const openOnboarding = () =>
    navigate({ to: "/audiolab/sales", search: { setup: "payout" } });

  return (
    <ContainerWrapper>
      <Flex
        direction="row"
        className="items-start justify-start lg:pt-[25px] pb-[24px]"
      >
        <LeftNav type="account" />
        <Flex direction="column" className="items-start w-full flex-1">
          <Flex className="w-full items-center justify-between mb-4 gap-3">
            <h1 className="text-[24px] font-semibold">Sales &amp; Earnings</h1>
            {!isLoadingCapabilities &&
              !showOnboarding &&
              capabilities?.onboardingComplete && <PayoutAccountStatus />}
          </Flex>
          {showOnboarding ? (
            <PayoutOnboardingRedirect />
          ) : (
            <SalesContent onSetupPayout={openOnboarding} />
          )}
        </Flex>
      </Flex>
    </ContainerWrapper>
  );
}

/** "Payout account · Active" in the header. The label opens the artist's
 * Stripe Express dashboard in a new tab. */
function PayoutAccountStatus() {
  const { mutateAsync: getDashboardLink, isPending } =
    useStripeControllerGetDashboardLink();

  async function openStripeDashboard() {
    // Opened within the tap: Safari blocks a popup opened after an await.
    const tab = window.open("", "_blank");
    if (tab) tab.opener = null;
    try {
      const { url } = await getDashboardLink();
      if (tab) tab.location.href = url;
      else window.location.assign(url);
    } catch {
      tab?.close();
      toast.error("Couldn't open your Stripe account. Please try again.");
    }
  }

  return (
    <Flex className="items-center gap-[8px] flex-none">
      <button
        type="button"
        onClick={openStripeDashboard}
        disabled={isPending}
        data-testid="sales-payout-account-link"
        className="text-[13px] font-semibold text-text-primary underline underline-offset-2 hover:text-brand-primary cursor-pointer outline-none"
      >
        Payout account
      </button>
      <span className="rounded-full bg-green-200 px-[10px] py-[3px] text-[12px] font-bold text-green-600">
        Active
      </span>
    </Flex>
  );
}

function SalesContent({ onSetupPayout }: { onSetupPayout: () => void }) {
  // Gates the new custom album sales table. While off, everyone keeps the
  // existing Stripe ConnectPayouts widget — flip the gate on after the
  // album-order financials backfill has run.
  const newTableEnabled = useGateValue("sales_earnings_table");
  const { data: capabilities, isLoading: isLoadingCapabilities } =
    useStripeControllerGetStripeAccountCapabilities();
  const { data: pendingTransfers, isLoading: isLoadingTransfers } =
    useStripeControllerGetPendingTransfersSummary();

  if (isLoadingCapabilities || isLoadingTransfers) {
    return (
      <Box className="bg-white rounded-lg p-8 border border-[rgba(5,12,43,0.06)] w-full">
        <Flex className="items-center gap-3 justify-center min-h-[200px]">
          <Spinner size="3" />
          <Text className="text-[14px] text-neutral-ink">Loading...</Text>
        </Flex>
      </Box>
    );
  }

  if (!capabilities?.hasPayoutAccount) {
    return <NoPayoutDashboard onSetupPayout={onSetupPayout} />;
  }

  const hasPending = (pendingTransfers?.count ?? 0) > 0;
  const onboardingComplete = capabilities.onboardingComplete;

  if (hasPending && !onboardingComplete) {
    return (
      <Flex direction="column" className="w-full gap-4">
        <PendingTransfersBanner
          totalAmountCents={pendingTransfers?.totalAmountCents ?? 0}
          count={pendingTransfers?.count ?? 0}
          currency={pendingTransfers?.currency ?? "usd"}
          onboardingComplete={false}
        />
      </Flex>
    );
  }

  // New custom album sales table (gated). Its pending-transfers banner is
  // rendered inside SalesDashboard from the sales summary, so it always matches
  // the Pending Payout card (same artist-scoped source) — no divergence.
  if (newTableEnabled) {
    return (
      <Flex direction="column" className="w-full gap-4">
        {!onboardingComplete && <OnboardingIncompleteNotice />}
        <SalesDashboard onboardingComplete={onboardingComplete} />
      </Flex>
    );
  }

  // Default: existing Stripe embedded payouts widget. No sales summary here, so
  // the banner keeps its own account-level (connect-id) pending source.
  return (
    <StripeConnectProvider>
      <Flex direction="column" className="w-full gap-4">
        {hasPending && (
          <PendingTransfersBanner
            totalAmountCents={pendingTransfers?.totalAmountCents ?? 0}
            count={pendingTransfers?.count ?? 0}
            currency={pendingTransfers?.currency ?? "usd"}
            onboardingComplete={onboardingComplete}
          />
        )}
        {!onboardingComplete && <OnboardingIncompleteNotice />}
        <EarningsDashboard />
      </Flex>
    </StripeConnectProvider>
  );
}

const EMPTY_SUMMARY: SalesSummaryDto = {
  netEarnedCents: 0,
  grossSalesCents: 0,
  pendingPayoutCents: 0,
  pendingPayoutCount: 0,
  totalUnitsSold: 0,
  netEarnedTrendPercent: null,
  currency: "usd",
};

/** Pre-payout Sales & Earnings: setup banner, zeroed summary, empty sales. */
function NoPayoutDashboard({ onSetupPayout }: { onSetupPayout: () => void }) {
  return (
    <Flex direction="column" className="w-full gap-4">
      <SetupPayoutBanner onSetupPayout={onSetupPayout} />
      <SalesSummaryCards summary={EMPTY_SUMMARY} />
      <NoSalesYet />
    </Flex>
  );
}

function SetupPayoutBanner({ onSetupPayout }: { onSetupPayout: () => void }) {
  return (
    // The whole banner starts the setup, not just the button (which is a span
    // for that reason — a nested button would be invalid markup).
    <button
      type="button"
      onClick={onSetupPayout}
      data-testid="sales-setup-payout"
      className="w-full cursor-pointer rounded-[12px] border border-[rgba(5,12,43,0.06)] bg-white p-[20px] text-left outline-none transition-shadow hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-primary"
    >
      <Flex className="w-full flex-col md:flex-row md:items-center gap-[20px] md:gap-[32px]">
        <Flex className="flex-1 items-center gap-[16px]">
          <img
            src={walletImage}
            alt=""
            className="w-[76px] h-[76px] shrink-0"
          />
          <Flex direction="column" className="gap-[4px]">
            <Text className="text-[16px] font-bold">
              Your sales will show up here
            </Text>
            <Text className="text-[13px] text-text-secondary">
              Once you start selling on Lutely, you&apos;ll be able to track
              your sales, earnings, and payouts here.
            </Text>
          </Flex>
        </Flex>
        <Flex
          direction="column"
          className="items-start gap-[4px] md:max-w-[280px]"
        >
          <Text className="text-[14px] font-bold">
            First, set up your payout account
          </Text>
          <Text className="text-[12px] text-text-secondary">
            Connect with Stripe so you&apos;re ready to receive money when you
            make a sale.
          </Text>
          <span className="mt-[6px] inline-flex items-center rounded-[8px] bg-brand-primary px-[14px] py-[8px] text-[13px] font-semibold text-white">
            Set up payout account
          </span>
        </Flex>
      </Flex>
    </button>
  );
}

function OnboardingIncompleteNotice() {
  return (
    <Box className="bg-blue-50 border border-blue-200 rounded-lg p-4 w-full">
      <Flex className="items-start gap-3">
        <Flex direction="column" gap="1" className="flex-1">
          <Text className="text-[14px] font-medium text-blue-800">
            Finish setting up your payout account
          </Text>
          <Text className="text-[13px] text-blue-700">
            We need your payout account setup complete before we can transfer
            earnings to your bank account.
          </Text>
        </Flex>
        <Button asChild size="1" variant="solid">
          <Link
            to="/audiolab/sales"
            search={{ setup: "payout" }}
            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-full cursor-pointer whitespace-nowrap"
          >
            Complete Setup
          </Link>
        </Button>
      </Flex>
    </Box>
  );
}

/** ?setup=payout: redirect to Stripe-hosted onboarding. location.replace keeps
 * this transient URL out of history; the refresh URL loops back through here. */
function PayoutOnboardingRedirect() {
  const navigate = useNavigate();
  const { mutateAsync: getOnboardingLink } =
    useStripeControllerGetOnboardingLink();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { url } = await getOnboardingLink({
          data: {
            returnUrl: `${window.location.origin}/audiolab/sales`,
            refreshUrl: `${window.location.origin}/audiolab/sales?setup=payout`,
          },
        });
        if (cancelled) return;
        analytics.track(EventEnum.STRIPE_CONNECT_ONBOARDING_STARTED);
        window.location.replace(url);
      } catch {
        if (cancelled) return;
        toast.error("Couldn't open Stripe. Please try again.");
        navigate({ to: "/audiolab/sales", search: {}, replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getOnboardingLink, navigate]);

  return (
    <Box className="bg-white rounded-lg p-8 border border-[rgba(5,12,43,0.06)] w-full">
      <Flex className="items-center gap-3 justify-center min-h-[200px]">
        <Spinner size="3" />
        <Text className="text-[14px] text-neutral-ink">
          Taking you to Stripe to set up your payout account...
        </Text>
      </Flex>
    </Box>
  );
}

function EarningsDashboard() {
  const { isLoading, isReady } = useStripeConnect();

  if (isLoading) {
    return (
      <Box className="bg-white rounded-lg p-8 border border-[rgba(5,12,43,0.06)] w-full">
        <Flex className="items-center gap-3 justify-center min-h-[200px]">
          <Spinner size="3" />
          <Text className="text-[14px] text-neutral-ink">
            Loading earnings...
          </Text>
        </Flex>
      </Box>
    );
  }

  if (!isReady) return null;

  return (
    <Flex direction="column" className="w-full gap-6 revert open-sans-regular">
      <ConnectPayouts />
    </Flex>
  );
}
